import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API, getToken } from './adminApi';
import ComposerPanel from './granularInterconnecting/ComposerPanel';
import SuggestionPreviewTabs from './granularInterconnecting/SuggestionPreviewTabs';
import RelationshipHistoryPanel, { pairKey } from './granularInterconnecting/RelationshipHistoryPanel';
import EmailHistoryPanel from './EmailHistoryPanel';
import MemberInfoPanel from './granularInterconnecting/MemberInfoPanel';
import MatchedIntrosPanel from './granularInterconnecting/MatchedIntrosPanel';
import RemindersPanel from './granularInterconnecting/RemindersPanel';

// Matches the current /respond page's own wording (greeting, "keen to
// connect" framing, contact-details reassurance) so the email and the page
// it links to read as one consistent voice — still fully editable per-send,
// this is just the starting point. Used both for the initial mount and by
// resetRoundState() (fired on every community switch), so it doesn't get
// wiped back to blank as soon as an admin picks a community.
const DEFAULT_SHARED_TEMPLATE = {
  subject: '{{firstName}}, these people are keen to connect!',
  body: "Hi {{firstName}}!<br><br>These people are keen to connect in {{communityName}}.<br>Would you like us to put you in touch?<br><br>We think you've got a few things in common. We won't share your contact details, and you can chat over Messenger 🙂<br><br>{{suggestedPeopleBlock}}<br>{{respondButton}}",
};

// Mirrors the fixed 4 keys adminEmail.js's own
// /suggestions/communities/:id/fields route always returns
// (firstName/age/gender/location) — kept here so the "everything ticked"
// default (see includedFields below) holds even before that route's
// response has loaded, not just after fetchFields runs.
const ALL_BASE_FIELD_KEYS = ['communityName', 'firstName', 'age', 'gender', 'location'];

// Straight-line distance in miles between two [lng, lat] points.
const distanceMiles = ([lng1, lat1], [lng2, lat2]) => {
  const rad = (d) => (d * Math.PI) / 180;
  const a = Math.sin(rad(lat2 - lat1) / 2) ** 2
    + Math.cos(rad(lat1)) * Math.cos(rad(lat2)) * Math.sin(rad(lng2 - lng1) / 2) ** 2;
  return 3958.8 * 2 * Math.asin(Math.sqrt(a));
};

const GranularInterconnectingEmails = () => {
  // 'suggestions': the existing AI-match composer below. 'matched-intros':
  // a separate view (MatchedIntrosPanel) for mutually-accepted matches
  // ready for an actual introduction. 'reminders': a separate view
  // (RemindersPanel) for following up with people from a past Suggestions
  // round who haven't (fully) responded — each is a different workflow
  // entirely, not another step in this one, so it's a top-level mode
  // switch rather than living inside the composer's own state.
  const [viewMode, setViewMode] = useState('suggestions');
  const [communities, setCommunities] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [communityTitle, setCommunityTitle] = useState('');
  const [members, setMembers] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');

  // Who you're emailing — the single selection step. Everyone in this set
  // is who gets introduced to each other (or hand-picked partners, in
  // 'separate' mode) — there's no separate "Suggest" checkbox anymore.
  const [emailedUserIds, setEmailedUserIds] = useState(new Set());
  const [groupMode, setGroupMode] = useState('together'); // 'together' | 'separate'
  const [perRecipientPartners, setPerRecipientPartners] = useState({}); // {userId: [partnerUserId,...]} — only meaningful in 'separate' mode
  // 'together' mode only — subset of emailedUserIds who stay in the mesh
  // (still suggested to, and suggested-to-others normally) but don't
  // actually get an email themselves this round. See suggestionRoundService.js.
  const [excludedRecipientIds, setExcludedRecipientIds] = useState(new Set());
  // Which /respond page copy this round's recipients get — see
  // suggestionResponseToken.js / RespondToSuggestion.js. 'v8' (default):
  // the current community-focused header. 'v2': acknowledges whoever they
  // already said yes to via the email's own per-person Connect button
  // before asking about the rest. 'v1': always the original generic
  // "Would you like us to put you in touch?" copy.
  const [respondPageVersion, setRespondPageVersion] = useState('v8');

  const [relationshipHistory, setRelationshipHistory] = useState([]);
  const [fields, setFields] = useState({ baseFields: [], communityFields: [] });
  // Everything ticked by default (see fetchFields below, and
  // resetRoundState/the saved-draft fallback, which must both match this
  // same "show everything" default — an admin who wants a leaner email
  // can untick fields, but starting from "show everything" means no one
  // has to remember which community-specific fields exist just to
  // include them). ALL_BASE_FIELD_KEYS mirrors the fixed 4 keys
  // adminEmail.js's /suggestions/communities/:id/fields always returns
  // (firstName/age/gender/location) — hardcoded here so this default
  // holds even before that route's response has loaded.
  const [includedFields, setIncludedFields] = useState({ base: ALL_BASE_FIELD_KEYS, community: [] });

  const [sharedTemplate, setSharedTemplate] = useState(DEFAULT_SHARED_TEMPLATE);
  // "From James" sender name + signature, added server-side (see
  // backend/services/emailSignature.js) — same as Reminders / Create Message.
  const [includeSignature, setIncludeSignature] = useState(true);
  const [sendAsMe, setSendAsMe] = useState(true);
  const [roundLabel, setRoundLabel] = useState(''); // optional name for this batch, shown in Round History

  const [previewPlan, setPreviewPlan] = useState(null);
  const [previewUpdating, setPreviewUpdating] = useState(false);
  // Guards against a stale preview response landing after a newer one —
  // the live-preview effect below debounces per change, but two separate
  // fetches (from two changes close enough together) can still overlap in
  // flight, and network timing alone decides which one's response arrives
  // last. Only the response matching the most recently *issued* request is
  // ever applied; anything older is silently dropped.
  const previewRequestIdRef = useRef(0);
  const [sendLoading, setSendLoading] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [actionError, setActionError] = useState('');

  const [showRoundHistory, setShowRoundHistory] = useState(false);
  const [roundHistory, setRoundHistory] = useState(null);
  const [roundHistoryLoading, setRoundHistoryLoading] = useState(false);
  const [expandedRoundId, setExpandedRoundId] = useState(null);
  const [viewingHistoryUserId, setViewingHistoryUserId] = useState(null);
  const [infoUserId, setInfoUserId] = useState(null);
  // Radius highlight: members within radiusMiles of the centre get a blue glow
  // ring (on top of, not instead of, their status colours). Centre is
  // 'selected' = anyone currently selected, or one member's userId.
  const [radiusOn, setRadiusOn] = useState(false);
  const [radiusMiles, setRadiusMiles] = useState(5);
  const [radiusCentre, setRadiusCentre] = useState('selected');
  // Sort-by-distance: one member's userId whose card goes first, with
  // everyone else ordered nearest to furthest (no location goes last).
  const [sortFromUserId, setSortFromUserId] = useState(null);

  // One auto-saved draft per (admin, community) — savedDraft is the raw
  // state fetched on community select; the banner offers to restore or
  // discard it. Once restored (or discarded, or a send succeeds) it's
  // cleared so the banner disappears.
  const [savedDraft, setSavedDraft] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState(null);

  const authedFetch = useCallback(async (path, options = {}) => {
    const token = await getToken();
    const res = await fetch(`${API}${path}`, {
      ...options,
      headers: { ...(options.headers || {}), Authorization: `Bearer ${token}` },
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || 'Request failed');
    return json;
  }, []);

  useEffect(() => {
    fetch(`${API}/communities`)
      .then((res) => res.json())
      .then((data) => setCommunities(Array.isArray(data)
        ? [...data].sort((a, b) => (a.title || '').localeCompare(b.title || ''))
        : []))
      .catch(() => setCommunities([]));
  }, []);

  const resetRoundState = () => {
    setEmailedUserIds(new Set());
    setGroupMode('together');
    setRespondPageVersion('v8');
    setPerRecipientPartners({});
    setExcludedRecipientIds(new Set());
    // Everything ticked — matches fetchFields' own "show everything"
    // default (see includedFields' initial state above for why). Uses
    // whatever fields.communityFields currently holds rather than
    // clearing it — if this reset is happening as part of switching
    // communities, fetchFields for the new community re-populates both
    // fields and includedFields right after anyway.
    setIncludedFields({ base: ALL_BASE_FIELD_KEYS, community: fields.communityFields });
    setSharedTemplate(DEFAULT_SHARED_TEMPLATE);
    setRoundLabel('');
    setPreviewPlan(null);
    setSendResult(null);
    setActionError('');
    setRelationshipHistory([]);
    setRoundHistory(null);
    setShowRoundHistory(false);
    setExpandedRoundId(null);
    setSavedDraft(null);
    setDraftSavedAt(null);
  };

  const fetchMembers = useCallback(async (communityId) => {
    if (!communityId) return;
    setLoading(true);
    setFetchError('');
    try {
      const data = await authedFetch(`/admin/communities/${communityId}/members`);
      setCommunityTitle(data.communityTitle);
      setMembers(data.members || []);
    } catch (err) {
      setFetchError(err.message);
      setMembers(null);
    } finally {
      setLoading(false);
    }
  }, [authedFetch]);

  const fetchFields = useCallback(async (communityId) => {
    try {
      const data = await authedFetch(`/admin/suggestions/communities/${communityId}/fields`);
      setFields(data);
      // Everything ticked by default — an admin who wants a leaner email
      // can untick fields, but starting from "show everything" means no
      // one has to remember which community-specific fields (Handicap,
      // Playing Frequency, etc.) exist just to include them.
      setIncludedFields({ base: data.baseFields || [], community: data.communityFields || [] });
    } catch (err) {
      setFields({ baseFields: [], communityFields: [] });
    }
  }, [authedFetch]);

  const fetchRelationshipHistory = useCallback(async (communityId) => {
    try {
      const data = await authedFetch(`/admin/suggestions/communities/${communityId}/relationship-history`);
      setRelationshipHistory(data.pairs || []);
    } catch (err) {
      setRelationshipHistory([]);
    }
  }, [authedFetch]);

  const historyByPairKey = new Map(relationshipHistory.map((p) => [pairKey(p.a, p.b), p]));

  // Traffic-light status of a not-yet-selected member RELATIVE to whoever
  // is currently selected ("Who are you emailing") — 'novel' (green, never
  // suggested with any of them), 'pending' (orange, some pending/accepted/
  // one-sided history with at least one of them), or 'rejected' (red,
  // already rejected by/rejected one of them — blocked from selection so
  // the admin can't accidentally re-suggest a dead pair). Returns null when
  // nothing is selected yet, since there's nothing to compare against.
  // Each member's distance (miles) to the nearest radius centre, for the
  // radius highlight. Members without a location aren't in the map.
  const radiusCentres = (members || []).filter((mm) => mm.coordinates
    && (radiusCentre === 'selected' ? emailedUserIds.has(mm.userId) : mm.userId === radiusCentre));
  const distanceByUserId = new Map();
  if (radiusOn && radiusCentres.length) {
    (members || []).forEach((mm) => {
      if (!mm.coordinates) return;
      distanceByUserId.set(mm.userId, Math.min(...radiusCentres.map((c) => distanceMiles(c.coordinates, mm.coordinates))));
    });
  }

  const sortFrom = sortFromUserId && (members || []).find((mm) => mm.userId === sortFromUserId && mm.coordinates);
  const distanceFromSort = new Map();
  if (sortFrom) {
    (members || []).forEach((mm) => {
      if (mm.coordinates) distanceFromSort.set(mm.userId, distanceMiles(sortFrom.coordinates, mm.coordinates));
    });
  }
  const orderedMembers = sortFrom
    ? [...members].sort((a, b) => {
      if (a.userId === sortFrom.userId) return -1;
      if (b.userId === sortFrom.userId) return 1;
      return (distanceFromSort.get(a.userId) ?? Infinity) - (distanceFromSort.get(b.userId) ?? Infinity);
    })
    : members;
  const formatMiles = (mi) => (mi < 10 ? mi.toFixed(1) : Math.round(mi));

  const getRelativeStatus = (memberId) => {
    if (emailedUserIds.size === 0) return null;
    let worst = 'novel';
    for (const selectedId of emailedUserIds) {
      if (selectedId === memberId) continue;
      const pair = historyByPairKey.get(pairKey(selectedId, memberId));
      if (!pair) continue;
      if (pair.status === 'rejected') return 'rejected';
      worst = 'pending';
    }
    return worst;
  };

  const fetchRoundHistory = useCallback(async (communityId) => {
    setRoundHistoryLoading(true);
    try {
      const data = await authedFetch(`/admin/suggestions/rounds?communityId=${communityId}`);
      setRoundHistory(data.rounds || []);
    } catch (err) {
      setRoundHistory([]);
    } finally {
      setRoundHistoryLoading(false);
    }
  }, [authedFetch]);

  const toggleRoundHistory = () => {
    const next = !showRoundHistory;
    setShowRoundHistory(next);
    if (next && roundHistory === null && selectedId) fetchRoundHistory(selectedId);
  };

  const fetchDraft = useCallback(async (communityId) => {
    try {
      const data = await authedFetch(`/admin/suggestions/draft?communityId=${communityId}`);
      setSavedDraft(data.state || null);
      setDraftSavedAt(data.updatedAt || null);
    } catch (err) {
      setSavedDraft(null);
    }
  }, [authedFetch]);

  const handleSelectCommunity = (e) => {
    const communityId = e.target.value;
    setSelectedId(communityId);
    setMembers(null);
    setSortFromUserId(null);
    setFetchError('');
    resetRoundState();
    if (communityId) {
      fetchMembers(communityId);
      fetchFields(communityId);
      fetchRelationshipHistory(communityId);
      fetchDraft(communityId);
    }
  };

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    setActionError('');
    try {
      await authedFetch('/admin/suggestions/draft', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ communityId: selectedId, state: buildRequestBody() }),
      });
      setSavedDraft(null); // this IS now the current on-screen state — nothing to offer restoring
      setDraftSavedAt(new Date().toISOString());
    } catch (err) {
      setActionError(err.message);
    } finally {
      setSavingDraft(false);
    }
  };

  const handleRestoreDraft = () => {
    if (!savedDraft) return;
    setEmailedUserIds(new Set(savedDraft.emailedUserIds || []));
    setGroupMode(savedDraft.groupMode || 'together');
    setRespondPageVersion(savedDraft.respondPageVersion || 'v8');
    setPerRecipientPartners(savedDraft.perRecipientPartners || {});
    setExcludedRecipientIds(new Set(savedDraft.excludedRecipientUserIds || []));
    setIncludedFields(savedDraft.includedFields || { base: ALL_BASE_FIELD_KEYS, community: fields.communityFields });
    setSharedTemplate(savedDraft.sharedTemplate || { subject: '', body: '' });
    setRoundLabel(savedDraft.label || '');
    setSavedDraft(null);
  };

  const handleDiscardDraft = async () => {
    setSavedDraft(null);
    setDraftSavedAt(null);
    try {
      await authedFetch(`/admin/suggestions/draft?communityId=${selectedId}`, { method: 'DELETE' });
    } catch (err) {
      // Non-critical — the banner's already gone from the admin's point of view either way.
    }
  };

  const toggleEmailed = (userId) => {
    setEmailedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
    // Deselecting someone entirely also drops them from the "not being
    // sent" set — otherwise re-selecting them later would silently bring
    // back a stale exclusion from earlier in the session.
    setExcludedRecipientIds((prev) => {
      if (!prev.has(userId)) return prev;
      const next = new Set(prev);
      next.delete(userId);
      return next;
    });
  };

  const toggleExcludedRecipient = (userId) => {
    setExcludedRecipientIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const buildRequestBody = () => ({
    communityId: selectedId,
    emailedUserIds: [...emailedUserIds],
    groupMode,
    respondPageVersion,
    perRecipientPartners,
    excludedRecipientUserIds: [...excludedRecipientIds],
    includedFields,
    sharedTemplate,
    includeSignature,
    sendAsMe,
    label: roundLabel,
  });

  const handlePreview = useCallback(async (body) => {
    const requestId = ++previewRequestIdRef.current;
    setPreviewUpdating(true);
    setActionError('');
    try {
      const plan = await authedFetch('/admin/suggestions/preview', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      // A newer request has since been issued — this response is stale
      // (e.g. it reflects the selection from before the most recent
      // exclusion toggle), so it must never overwrite the current plan.
      if (requestId !== previewRequestIdRef.current) return;
      setPreviewPlan(plan);
    } catch (err) {
      if (requestId !== previewRequestIdRef.current) return;
      setActionError(err.message);
    } finally {
      if (requestId === previewRequestIdRef.current) setPreviewUpdating(false);
    }
  }, [authedFetch]);

  // Live-updating preview: the draft body reflects the current selection
  // within ~500ms with no manual "Preview" click needed. Deliberately calls
  // the same backend render path used by /suggestions/send (not a
  // client-side reimplementation) — that's exactly where the contact-info
  // enforcement lives, and duplicating it in the browser would create a
  // second, weaker copy of security-sensitive code for no real benefit.
  const requestBodySignature = JSON.stringify(buildRequestBody());
  useEffect(() => {
    if (!selectedId || emailedUserIds.size === 0) {
      setPreviewPlan(null);
      return undefined;
    }
    const timer = setTimeout(() => {
      handlePreview(JSON.parse(requestBodySignature));
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestBodySignature, selectedId, emailedUserIds.size]);

  const handleSend = async () => {
    if (!previewPlan) return;
    const confirmed = window.confirm(
      `Send ${previewPlan.recipients.length} email(s) now and create any configured suggestions? This cannot be undone.`
    );
    if (!confirmed) return;

    setSendLoading(true);
    setActionError('');
    try {
      const result = await authedFetch('/admin/suggestions/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildRequestBody()),
      });
      setSendResult(result);
      if (showRoundHistory) fetchRoundHistory(selectedId);
      // A sent round is no longer a "draft" of anything — clear it out so a
      // stale banner never offers to restore an already-sent setup.
      authedFetch(`/admin/suggestions/draft?communityId=${selectedId}`, { method: 'DELETE' }).catch(() => {});
    } catch (err) {
      setActionError(err.message);
    } finally {
      setSendLoading(false);
    }
  };

  const emailedMembers = (members || []).filter((m) => emailedUserIds.has(m.userId));

  return (
    <div className="as-signups">
      {fetchError && <div className="as-error">{fetchError}</div>}
      {actionError && <div className="as-error">{actionError}</div>}

      <div className="as-activity-toolbar">
        <select className="as-filter-tab" value={selectedId} onChange={handleSelectCommunity}>
          <option value="">Select a community…</option>
          {communities.map((c) => (
            <option key={c._id} value={c._id}>{c.title}</option>
          ))}
        </select>
        {selectedId && (
          <button className="as-filter-tab" onClick={() => { fetchMembers(selectedId); fetchFields(selectedId); fetchRelationshipHistory(selectedId); }}>↻ Refresh</button>
        )}
      </div>

      <div className="as-suggestion-mode-toggle" style={{ marginBottom: '1rem' }}>
        <button
          type="button"
          className={`as-filter-tab ${viewMode === 'suggestions' ? 'as-filter-tab--active' : ''}`}
          onClick={() => setViewMode('suggestions')}
        >
          Suggestions
        </button>
        <button
          type="button"
          className={`as-filter-tab ${viewMode === 'matched-intros' ? 'as-filter-tab--active' : ''}`}
          onClick={() => setViewMode('matched-intros')}
        >
          Matched Intros
        </button>
        <button
          type="button"
          className={`as-filter-tab ${viewMode === 'reminders' ? 'as-filter-tab--active' : ''}`}
          onClick={() => setViewMode('reminders')}
        >
          Reminders
        </button>
      </div>

      {loading && viewMode === 'suggestions' && <div className="as-loading">Loading…</div>}

      {viewMode === 'matched-intros' && (
        selectedId
          ? <MatchedIntrosPanel communityId={selectedId} communityTitle={communityTitle} authedFetch={authedFetch} />
          : <div className="as-empty">Select a community above first.</div>
      )}

      {viewMode === 'reminders' && (
        selectedId
          ? <RemindersPanel communityId={selectedId} communityTitle={communityTitle} authedFetch={authedFetch} />
          : <div className="as-empty">Select a community above first.</div>
      )}

      {viewMode === 'suggestions' && members && (
        <>
          <h2 className="as-section-title">{communityTitle} — Interconnecting ({members.length})</h2>
          {members.length === 0 && <div className="as-empty">No members in this community.</div>}

          {savedDraft && (
            <div className="as-draft-banner">
              <span>💾 You have a saved draft for this community.</span>
              <button type="button" className="as-filter-tab" onClick={handleRestoreDraft}>Restore</button>
              <button type="button" className="as-filter-tab" onClick={handleDiscardDraft}>Discard</button>
            </div>
          )}

          <div className="as-card-meta" style={{ marginBottom: '0.5rem' }}>Who are you emailing:</div>
          <div className="as-radius-tool">
            <label className="as-suggestion-checkbox-label">
              <input type="checkbox" checked={radiusOn} onChange={(e) => setRadiusOn(e.target.checked)} />
              Highlight people within
            </label>
            <input type="range" min="1" max="125" value={radiusMiles} onChange={(e) => setRadiusMiles(Number(e.target.value))} disabled={!radiusOn} />
            <input type="number" min="1" max="12500" value={radiusMiles} onChange={(e) => setRadiusMiles(Math.max(1, Number(e.target.value) || 1))} disabled={!radiusOn} style={{ width: '4.5rem' }} />
            <span>miles of</span>
            <select value={radiusCentre} onChange={(e) => setRadiusCentre(e.target.value)} disabled={!radiusOn}>
              <option value="selected">anyone you&apos;ve selected</option>
              {members.filter((mm) => mm.coordinates).map((mm) => (
                <option key={mm.userId} value={mm.userId}>{mm.firstName || '(no name)'}{mm.localArea ? ` — ${mm.localArea}` : ''}</option>
              ))}
            </select>
            {radiusOn && (
              <span className="as-card-meta">
                {radiusCentres.length === 0
                  ? (radiusCentre === 'selected' ? 'Select someone with a location to measure from.' : 'That person has no location.')
                  : `${members.filter((mm) => distanceByUserId.has(mm.userId) && distanceByUserId.get(mm.userId) <= radiusMiles && !radiusCentres.some((c) => c.userId === mm.userId)).length} within ${radiusMiles} miles · ${members.filter((mm) => !mm.coordinates).length} have no location`}
              </span>
            )}
          </div>
          {sortFrom && (
            <div className="as-radius-tool">
              <span>↕ Sorted nearest to furthest from <strong>{sortFrom.firstName || '(no name)'}</strong>{sortFrom.localArea ? ` (${sortFrom.localArea})` : ''}</span>
              <button type="button" className="as-filter-tab" onClick={() => setSortFromUserId(null)}>Clear sort</button>
            </div>
          )}
          {emailedUserIds.size > 0 && (
            <div className="as-signup-legend">
              <span><i className="as-signup-legend-dot as-signup-legend-dot--novel" /> never suggested with them</span>
              <span><i className="as-signup-legend-dot as-signup-legend-dot--pending" /> pending / prior history</span>
              <span><i className="as-signup-legend-dot as-signup-legend-dot--rejected" /> already rejected — can't select</span>
            </div>
          )}
          <div className="as-signup-list as-signup-list--grid">
            {orderedMembers.map((m) => {
              const isEmailed = emailedUserIds.has(m.userId);
              // Computed regardless of selection — a selected member can
              // still have a status relative to the OTHER selected members
              // (getRelativeStatus already skips comparing a member to itself).
              const relativeStatus = getRelativeStatus(m.userId);
              // Only blocks NEW selection — an already-selected card stays
              // clickable-to-deselect even if it turns out to be 'rejected'
              // against another selected member.
              const isBlockedFromSelecting = !isEmailed && relativeStatus === 'rejected';
              const isRadiusCentre = radiusOn && radiusCentres.some((c) => c.userId === m.userId);
              const distance = distanceByUserId.get(m.userId);
              const isInRadius = radiusOn && !isRadiusCentre && distance !== undefined && distance <= radiusMiles;
              const cardClassName = [
                'as-signup-card',
                !isBlockedFromSelecting && 'as-signup-card--clickable',
                isBlockedFromSelecting && 'as-signup-card--not-selectable',
                relativeStatus === 'novel' && 'as-signup-card--novel',
                relativeStatus === 'pending' && 'as-signup-card--pending-history',
                relativeStatus === 'rejected' && 'as-signup-card--blocked',
                isEmailed && 'as-signup-card--selected',
                isInRadius && 'as-signup-card--in-radius',
              ].filter(Boolean).join(' ');

              return (
                <div
                  key={m.userId}
                  className={cardClassName}
                  onClick={() => { if (!isBlockedFromSelecting) toggleEmailed(m.userId); }}
                >
                  <div className="as-signup-select">
                    <input
                      type="checkbox"
                      checked={isEmailed}
                      disabled={isBlockedFromSelecting}
                      onChange={() => toggleEmailed(m.userId)}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </div>
                  {m.photo ? <img src={m.photo} alt="" className="as-signup-img" /> : <div className="as-signup-img-placeholder">?</div>}
                  <div className="as-signup-details">
                    <div className="as-signup-headline">
                      <strong>{m.firstName || '(no name)'}</strong>
                      {m.age != null && <span className="as-signup-age">{m.age}</span>}
                      {m.gender && <span className="as-card-meta"> · {m.gender}</span>}
                    </div>
                    <div className="as-card-meta">
                      {m.localArea || 'Location not set'}
                      {isRadiusCentre && <span className="as-radius-badge"> · 📍 centre</span>}
                      {radiusOn && !isRadiusCentre && distance !== undefined && (
                        <span className={isInRadius ? 'as-radius-badge' : undefined}> · {formatMiles(distance)} mi</span>
                      )}
                      {sortFrom?.userId === m.userId && <span className="as-radius-badge"> · ↕ sorting from here</span>}
                      {sortFrom && sortFrom.userId !== m.userId && distanceFromSort.has(m.userId)
                        && !(radiusOn && distance !== undefined) && <span> · {formatMiles(distanceFromSort.get(m.userId))} miles away</span>}
                    </div>
                    {relativeStatus === 'rejected' && <div className="as-signup-blocked-note">Already rejected a selected person</div>}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem' }}>
                      <button type="button" className="as-text-link" onClick={(e) => { e.stopPropagation(); setInfoUserId(m.userId); }}>Full info</button>
                      {m.coordinates && sortFrom?.userId !== m.userId && (
                        <button type="button" className="as-text-link" onClick={(e) => { e.stopPropagation(); setSortFromUserId(m.userId); }}>Sort nearest to here</button>
                      )}
                      <button type="button" className="as-text-link" onClick={(e) => { e.stopPropagation(); setViewingHistoryUserId(m.userId); }}>View email history</button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {emailedUserIds.size > 0 && (
            <>
              <div className="as-suggestion-mode-toggle" style={{ marginTop: '1rem' }}>
                <button type="button" className={`as-filter-tab ${groupMode === 'together' ? 'as-filter-tab--active' : ''}`} onClick={() => setGroupMode('together')}>
                  Suggest all together
                </button>
                <button type="button" className={`as-filter-tab ${groupMode === 'separate' ? 'as-filter-tab--active' : ''}`} onClick={() => setGroupMode('separate')}>
                  Email separate suggestions for each one
                </button>
              </div>

              {groupMode === 'together' && (
                <div style={{ marginTop: '0.75rem' }}>
                  <div className="as-card-meta" style={{ marginBottom: '0.4rem' }}>
                    Who actually gets an email — everyone selected above is still suggested to the others either way. Click a name to move it.
                  </div>
                  <table className="as-ab-table">
                    <thead>
                      <tr>
                        <th>Being sent</th>
                        <th>Not being sent</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td style={{ verticalAlign: 'top' }}>
                          {emailedMembers.filter((m) => !excludedRecipientIds.has(m.userId)).map((m) => (
                            <button
                              key={m.userId}
                              type="button"
                              className="as-filter-tab"
                              style={{ margin: '0.2rem' }}
                              onClick={() => toggleExcludedRecipient(m.userId)}
                              title="Click to stop emailing this person (still suggested to the others)"
                            >
                              {m.firstName || '(no name)'}
                            </button>
                          ))}
                        </td>
                        <td style={{ verticalAlign: 'top' }}>
                          {emailedMembers.filter((m) => excludedRecipientIds.has(m.userId)).map((m) => (
                            <button
                              key={m.userId}
                              type="button"
                              className="as-filter-tab"
                              style={{ margin: '0.2rem' }}
                              onClick={() => toggleExcludedRecipient(m.userId)}
                              title="Click to email this person again"
                            >
                              {m.firstName || '(no name)'}
                            </button>
                          ))}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              <div className="as-suggestion-mode-toggle" style={{ marginTop: '0.5rem' }}>
                <span className="as-card-meta" style={{ marginRight: '0.5rem' }}>/respond page:</span>
                <button
                  type="button"
                  className={`as-filter-tab ${respondPageVersion === 'v2' ? 'as-filter-tab--active' : ''}`}
                  onClick={() => setRespondPageVersion('v2')}
                  title="Acknowledges whoever they already said yes to via the email's Connect button, then asks about the rest"
                >
                  Version 2 (smart greeting)
                </button>
                <button
                  type="button"
                  className={`as-filter-tab ${respondPageVersion === 'v1' ? 'as-filter-tab--active' : ''}`}
                  onClick={() => setRespondPageVersion('v1')}
                  title="Always the original generic &quot;Would you like us to put you in touch?&quot; wording"
                >
                  Version 1 (original)
                </button>
                <button
                  type="button"
                  className={`as-filter-tab ${respondPageVersion === 'v3' ? 'as-filter-tab--active' : ''}`}
                  onClick={() => setRespondPageVersion('v3')}
                  title="Same acknowledge-then-ask logic as v2, but typed out on the page and naming exactly who's left to answer"
                >
                  Version 3 (typed reveal)
                </button>
                <button
                  type="button"
                  className={`as-filter-tab ${respondPageVersion === 'v7' ? 'as-filter-tab--active' : ''}`}
                  onClick={() => setRespondPageVersion('v7')}
                  title="No typed reveal, always the generic &quot;Click connect below&quot; wording, and ends with a plain Thanks! (no Close window button)"
                >
                  Version 7 (plain, no close button)
                </button>
                <button
                  type="button"
                  className={`as-filter-tab ${respondPageVersion === 'v8' ? 'as-filter-tab--active' : ''}`}
                  onClick={() => setRespondPageVersion('v8')}
                  title="Same mechanics as v7 (no typed reveal, no close button), but with community-focused header wording instead of naming who was suggested"
                >
                  Version 8 (community-focused header)
                </button>
                <button
                  type="button"
                  className={`as-filter-tab ${respondPageVersion === 'v9' ? 'as-filter-tab--active' : ''}`}
                  onClick={() => setRespondPageVersion('v9')}
                  title="Same as v8, but the header asks &quot;Would you be interested in connecting with any of these people in ...?&quot; instead of saying they're keen to connect"
                >
                  Version 9 (invites them to choose)
                </button>
              </div>
              <div className="as-card-meta" style={{ marginTop: '0.35rem' }}>
                Try it as a member would (sample people, nothing saved):{' '}
                {['v2', 'v1', 'v3', 'v7', 'v8', 'v9'].map((v, i) => (
                  <React.Fragment key={v}>
                    {i > 0 && ' · '}
                    <a href={`/respond?simulate=${v}${selectedId ? `&community=${selectedId}` : ''}`} target="_blank" rel="noopener noreferrer">
                      {v === respondPageVersion ? <strong>{v} ↗</strong> : `${v} ↗`}
                    </a>
                  </React.Fragment>
                ))}
              </div>

              <RelationshipHistoryPanel
                suggestedMembers={emailedMembers}
                historyByPairKey={historyByPairKey}
              />

              <div className="as-email-field">
                <label>Round name (optional — shown in Round History, not sent to recipients)</label>
                <input type="text" value={roundLabel} onChange={(e) => setRoundLabel(e.target.value)} placeholder="e.g. August Golf Mixer intros" />
              </div>

              <ComposerPanel
                fields={fields}
                includedFields={includedFields}
                setIncludedFields={setIncludedFields}
                sharedTemplate={sharedTemplate}
                setSharedTemplate={setSharedTemplate}
                includeSignature={includeSignature}
                setIncludeSignature={setIncludeSignature}
                sendAsMe={sendAsMe}
                setSendAsMe={setSendAsMe}
                communityId={selectedId}
                authedFetch={authedFetch}
              />

              <div className="as-email-preview">
                <div className="as-card-meta">
                  {previewUpdating ? 'Updating preview…' : 'Preview'}
                  <button className="as-filter-tab" style={{ marginLeft: '0.75rem' }} onClick={handleSaveDraft} disabled={savingDraft}>
                    {savingDraft ? 'Saving…' : '💾 Save Draft'}
                  </button>
                  {draftSavedAt && <span className="as-card-meta" style={{ marginLeft: '0.5rem' }}>Draft saved</span>}
                  {previewPlan && (
                    <button className="as-btn as-btn--approve" style={{ marginLeft: '0.75rem' }} onClick={handleSend} disabled={sendLoading || previewUpdating || previewPlan.recipients.length === 0}>
                      {sendLoading ? 'Sending…' : `📤 Send (${previewPlan.recipients.length})`}
                    </button>
                  )}
                </div>
                {previewPlan?.warnings.length > 0 && (
                  <ul className="as-suggestion-warnings">
                    {previewPlan.warnings.map((w, i) => <li key={i}>⚠ {w}</li>)}
                  </ul>
                )}
                {previewPlan && (
                  <>
                    <div className="as-email-preview-from">From: {previewPlan.community.senderAddress}</div>
                    <SuggestionPreviewTabs
                      previewPlan={previewPlan}
                      groupMode={groupMode}
                      members={members}
                      perRecipientPartners={perRecipientPartners}
                      setPerRecipientPartners={setPerRecipientPartners}
                    />
                  </>
                )}
              </div>

              {sendResult && (
                <div className="as-action-msg">
                  {sendResult.dryRun ? '🧪 DRY RUN — ' : '✅ '}
                  Attempted {sendResult.attempted}: {sendResult.sent} sent, {sendResult.dry_run} dry-run, {sendResult.failed} failed.
                  {sendResult.failed > 0 && sendResult.firstError && (
                    <div className="as-error" style={{ marginTop: '0.5rem' }}>Why it failed: {sendResult.firstError}</div>
                  )}
                </div>
              )}
            </>
          )}

          <div className="as-activity-toolbar" style={{ marginTop: '1.5rem' }}>
            <button className="as-filter-tab" onClick={toggleRoundHistory}>
              {showRoundHistory ? '▾' : '▸'} Round History
            </button>
          </div>

          {showRoundHistory && (
            <div>
              {roundHistoryLoading && <div className="as-loading">Loading…</div>}
              {roundHistory && roundHistory.length === 0 && <div className="as-empty">No rounds sent yet for this community.</div>}
              {roundHistory && roundHistory.length > 0 && (
                <table className="as-ab-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>When</th>
                      <th>Sent by</th>
                      <th>Mode</th>
                      <th>Recipients</th>
                    </tr>
                  </thead>
                  <tbody>
                    {roundHistory.map((round) => (
                      <React.Fragment key={round._id}>
                        <tr style={{ cursor: 'pointer' }} onClick={() => setExpandedRoundId(expandedRoundId === round._id ? null : round._id)}>
                          <td>{round.label || <span className="as-card-meta">(unnamed)</span>}</td>
                          <td>{new Date(round.createdAt).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
                          <td>{round.createdBy}</td>
                          <td>{round.mode} / {round.composerMode}</td>
                          <td>{round.recipients.length}</td>
                        </tr>
                        {expandedRoundId === round._id && round.recipients.map((r, i) => (
                          <tr key={i}>
                            <td colSpan={5}>
                              <div className="as-suggestion-recipient-card">
                                <div className="as-card-meta"><strong>To:</strong> {r.email} — {r.status}{r.errorMessage ? ` (${r.errorMessage})` : ''}</div>
                                <div className="as-card-meta"><strong>Subject:</strong> {r.subject}</div>
                                <div className="as-suggestion-body-preview" dangerouslySetInnerHTML={{ __html: r.htmlBody }} />
                              </div>
                            </td>
                          </tr>
                        ))}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </>
      )}

      {infoUserId && members?.find((mm) => mm.userId === infoUserId) && (
        <MemberInfoPanel
          member={members.find((mm) => mm.userId === infoUserId)}
          communityTitle={communityTitle}
          authedFetch={authedFetch}
          onClose={() => setInfoUserId(null)}
        />
      )}
      {viewingHistoryUserId && (
        <EmailHistoryPanel userId={viewingHistoryUserId} onClose={() => setViewingHistoryUserId(null)} />
      )}
    </div>
  );
};

export default GranularInterconnectingEmails;
